import { useSettlementToken } from "features/tokens/useSettlementToken";
import React, { useState } from "react";
import "./issuer-page.css";
import KpiCard from "components/kpi-card/KpiCard";
import CardsList from "features/cards/cards-list/CardsList";
import MerchantsList from "features/merchants/merchants-list/MerchantsList";
import TerminalsList from "features/terminals/terminals-list/TerminalsList";
import IssueCardForm from "features/cards/IssueCardForm";
import AddMerchantForm from "features/merchants/AddMerchantForm";
import RedeemList from "features/redeem/redeem-list/RedeemList";
import LoadingModal from "modals/loading/LoadingModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import { useIssuerCards } from "./useIssuerCards";
import { useIssuerMerchants } from "./useIssuerMerchants";
import { useIssuerTerminals } from "./useIssuerTerminals";
import { useIssuerKpis } from "./useIssuerKpis";
import { useRedeemData, REJECT_REASON } from "features/redeem/useRedeemData";
import { apiErrorMessage } from "utils/errors/parseApiError";

function IssuerPage() {
  const { label: tokenSymbol } = useSettlementToken();
  const [showIssueCard, setShowIssueCard] = useState(false);
  const [showAddMerchant, setShowAddMerchant] = useState(false);
  // Set when resuming signup for a merchant that's on-chain but not signed up.
  const [resumeMerchantId, setResumeMerchantId] = useState<string | undefined>(
    undefined,
  );
  // Bumped on every open so AddMerchantForm remounts with fresh (re-seeded) state.
  const [addMerchantNonce, setAddMerchantNonce] = useState(0);

  const { cards, loadingCards } = useIssuerCards();
  const { merchants, activeMerchants, loadingMerchants, refresh } =
    useIssuerMerchants();
  const { terminals, loadingTerminals } = useIssuerTerminals();

  const openAddMerchant = () => {
    setResumeMerchantId(undefined);
    setAddMerchantNonce((n) => n + 1);
    setShowAddMerchant(true);
  };
  const openResumeSignup = (merchantId: string) => {
    setResumeMerchantId(merchantId);
    setAddMerchantNonce((n) => n + 1);
    setShowAddMerchant(true);
  };
  const kpi = useIssuerKpis();

  const {
    requests,
    requestsLoading,
    refreshRequests,
    approveRedeem,
    rejectRedeem,
  } = useRedeemData();

  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  function handleApprove(
    requestId: string,
    merchantId: string,
    stripeAmount: number,
  ) {
    setProcessingId(requestId);
    setShowLoading(true);
    approveRedeem(
      { requestId, merchantId, stripeAmount },
      {
        onSuccess: () => {
          setProcessingId(null);
          setShowLoading(false);
          setSuccessMsg(
            `Redeem request approved. ${tokenSymbol} transfer complete.`,
          );
          setShowSuccess(true);
        },
        onError: (err: any) => {
          setProcessingId(null);
          setShowLoading(false);
          setErrorMsg(
            apiErrorMessage(err, "Failed to approve redeem request."),
          );
          setShowError(true);
        },
      },
    );
  }

  function handleReject(requestId: string) {
    setProcessingId(requestId);
    setShowLoading(true);
    rejectRedeem(
      { requestId },
      {
        onSuccess: () => {
          setProcessingId(null);
          setShowLoading(false);
          setSuccessMsg("Redeem request rejected.");
          setShowSuccess(true);
        },
        onError: (err: any) => {
          setProcessingId(null);
          setShowLoading(false);
          setErrorMsg(apiErrorMessage(err, "Failed to reject redeem request."));
          setShowError(true);
        },
      },
    );
  }

  return (
    <div className="ip-page">
      {/* ── KPI Grid ── */}
      <div className="ip-kpi-grid">
        <KpiCard
          scope="Cards"
          dotColor="var(--brand-hi)"
          amount={kpi.totalCards}
          loading={kpi.cardsCountLoading}
        />
        <KpiCard
          scope="Merchants"
          dotColor="var(--brand-hi)"
          amount={kpi.totalMerchants}
          loading={kpi.merchantsCountLoading}
        />
        <KpiCard
          scope="Terminals"
          dotColor="var(--brand-hi)"
          amount={kpi.totalTerminals}
          loading={kpi.terminalsCountLoading}
        />
        <KpiCard
          scope="Redeemed balance"
          dotColor="var(--pos)"
          amount={
            <>
              {kpi.balanceRedeemed.toLocaleString("en", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              <small className="ip-kpi-unit"> {tokenSymbol}</small>
            </>
          }
          loading={kpi.loadingRedeemedBalance}
        />
        {/* <KpiCard
          scope={kpi.deployedSupplyConfigured ? "Deployed" : "Treasury held"}
          dotColor="var(--copper)"
          amount={
            <>
              {kpi.balanceDeployed.toLocaleString("en", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              <small className="ip-kpi-unit"> {tokenSymbol}</small>
            </>
          }
          loading={kpi.loadingBalance}
        /> */}
      </div>

      {/* ── Redeem requests ── */}
      <section className="ip-section">
        <RedeemList
          requests={requests}
          loading={requestsLoading}
          processingId={processingId}
          onRefresh={refreshRequests}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      </section>

      {/* ── Cards section ── */}
      <section className="ip-section">
        <CardsList
          cards={cards}
          loading={loadingCards}
          onIssueCard={() => setShowIssueCard(true)}
        />
      </section>

      {/* ── Merchants section ── */}
      <section className="ip-section">
        <MerchantsList
          merchants={merchants}
          activeMerchants={activeMerchants}
          loading={loadingMerchants}
          onAddMerchant={openAddMerchant}
          onSignUp={openResumeSignup}
        />
      </section>

      {/* ── Terminals section ── */}
      <section className="ip-section">
        <TerminalsList terminals={terminals} loading={loadingTerminals} />
      </section>

      <IssueCardForm
        isOpen={showIssueCard}
        onClose={() => setShowIssueCard(false)}
      />

      <AddMerchantForm
        key={addMerchantNonce}
        isOpen={showAddMerchant}
        resumeMerchantId={resumeMerchantId}
        onClose={() => setShowAddMerchant(false)}
        onCompleted={refresh}
      />

      {/* ── Modals ── */}
      <LoadingModal isOpen={showLoading} loadingMsg="Processing…" />

      {showSuccess && (
        <SuccessModal
          isOpen={showSuccess}
          message={successMsg}
          handleClose={() => setShowSuccess(false)}
        />
      )}

      {showError && (
        <ErrorModal
          isOpen={showError}
          errorMsg={errorMsg}
          handleClose={() => setShowError(false)}
        />
      )}
    </div>
  );
}

export default IssuerPage;
